/**
 * useCanvasActions - Canvas operation callbacks
 *
 * Provides onChange, onExportToBackend, zoom, undo/redo handlers.
 *
 * Extracted from App.tsx lines 818–1079
 */

import { useState, useCallback } from "react";

import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { t } from "@excalidraw/excalidraw/i18n";
import { newElementWith } from "@excalidraw/element";

import type {
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  UIAppState,
} from "@excalidraw/excalidraw/types";

import CustomStats from "../CustomStats";
import { LocalData } from "../data/LocalData";
import { exportToBackend } from "../data";
import { debugRenderer } from "../components/DebugCanvas";

interface UseCanvasActionsParams {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  collabAPI: any;
  authSession: { user: { id: string } | null } | null;
  diagramSync: {
    currentDiagramId: string | null;
    saveToCloud: (elements: any, appState: any, name: any, files: any) => void;
  };
  debugCanvasRef: React.RefObject<HTMLCanvasElement | null>;
}

export const useCanvasActions = ({
  excalidrawAPI,
  collabAPI,
  authSession,
  diagramSync,
  debugCanvasRef,
}: UseCanvasActionsParams) => {
  const [currentZoom, setCurrentZoom] = useState(1);
  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState("");

  const onChange = useCallback(
    (
      elements: readonly OrderedExcalidrawElement[],
      appState: AppState,
      files: BinaryFiles,
    ) => {
      // Update zoom state for the custom footer
      if (appState.zoom?.value !== undefined) {
        setCurrentZoom(appState.zoom.value);
      }

      if (collabAPI?.isCollaborating()) {
        collabAPI.syncElements(elements);
      }

      // Cloud sync for authenticated users
      if (authSession?.user && diagramSync.currentDiagramId) {
        if (import.meta.env.DEV) {
          console.log(
            "[useCanvasActions] onChange triggered: saving",
            elements.length,
            "elements to diagram",
            diagramSync.currentDiagramId,
          );
        }
        diagramSync.saveToCloud(elements, appState, undefined, files);
      }

      if (!LocalData.isSavePaused()) {
        LocalData.save(elements, appState, files, () => {
          if (excalidrawAPI) {
            let didChange = false;

            const updatedElements = excalidrawAPI
              .getSceneElementsIncludingDeleted()
              .map((element) => {
                if (
                  LocalData.fileStorage.shouldUpdateImageElementStatus(element)
                ) {
                  const newElement = newElementWith(element, {
                    status: "saved",
                  });
                  if (newElement !== element) {
                    didChange = true;
                  }
                  return newElement;
                }
                return element;
              });

            if (didChange) {
              excalidrawAPI.updateScene({
                elements: updatedElements,
                captureUpdate: CaptureUpdateAction.NEVER,
              });
            }
          }
        });
      }

      // Render the debug scene if the debug canvas is available
      if (debugCanvasRef.current && excalidrawAPI) {
        debugRenderer(
          debugCanvasRef.current,
          appState,
          elements,
          window.devicePixelRatio,
        );
      }
    },
    [excalidrawAPI, collabAPI, authSession, diagramSync, debugCanvasRef],
  );

  const onExportToBackend = useCallback(
    async (
      exportedElements: readonly NonDeletedExcalidrawElement[],
      appState: Partial<AppState>,
      files: BinaryFiles,
    ) => {
      if (exportedElements.length === 0) {
        throw new Error(t("alerts.cannotExportEmptyCanvas"));
      }
      try {
        const { url, errorMessage: errMsg } = await exportToBackend(
          exportedElements,
          {
            ...appState,
            viewBackgroundColor: appState.exportBackground
              ? appState.viewBackgroundColor
              : getDefaultAppState().viewBackgroundColor,
          },
          files,
        );

        if (errMsg) {
          throw new Error(errMsg);
        }

        if (url) {
          setLatestShareableLink(url);
        }
      } catch (error: any) {
        if (error.name !== "AbortError") {
          const { width, height } = appState;
          console.error(error, {
            width,
            height,
            devicePixelRatio: window.devicePixelRatio,
          });
          throw new Error(error.message);
        }
      }
    },
    [],
  );

  const renderCustomStats = useCallback(
    (
      elements: readonly NonDeletedExcalidrawElement[],
      appState: UIAppState,
    ) => {
      return (
        <CustomStats
          setToast={(message) => excalidrawAPI!.setToast({ message })}
          appState={appState}
          elements={elements}
        />
      );
    },
    [excalidrawAPI],
  );

  const handleExport = useCallback(() => {
    if (excalidrawAPI) {
      excalidrawAPI.updateScene({
        appState: { openDialog: { name: "imageExport" } },
      });
    }
  }, [excalidrawAPI]);

  const handleZoomIn = useCallback(() => {
    if (excalidrawAPI) {
      const zoom = excalidrawAPI.getAppState().zoom.value;
      excalidrawAPI.updateScene({
        appState: { zoom: { value: Math.min(zoom * 1.1, 30) as any } },
      });
    }
  }, [excalidrawAPI]);

  const handleZoomOut = useCallback(() => {
    if (excalidrawAPI) {
      const zoom = excalidrawAPI.getAppState().zoom.value;
      excalidrawAPI.updateScene({
        appState: { zoom: { value: Math.max(zoom / 1.1, 0.1) as any } },
      });
    }
  }, [excalidrawAPI]);

  const handleUndo = useCallback(() => {
    const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const event = new KeyboardEvent("keydown", {
      key: "z",
      code: "KeyZ",
      ctrlKey: !isMac,
      metaKey: isMac,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  }, []);

  const handleRedo = useCallback(() => {
    const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const event = new KeyboardEvent("keydown", {
      key: "z",
      code: "KeyZ",
      ctrlKey: !isMac,
      metaKey: isMac,
      shiftKey: true,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  }, []);

  return {
    currentZoom,
    latestShareableLink,
    setLatestShareableLink,
    errorMessage,
    setErrorMessage,
    onChange,
    onExportToBackend,
    renderCustomStats,
    handleExport,
    handleZoomIn,
    handleZoomOut,
    handleUndo,
    handleRedo,
  };
};
